import { useEffect, useState } from 'react';
import { api } from '../api';

type DemoSession = {
  token: string;
  label: string | null;
  expires_at: string;
  created_at: string;
  created_by: string;
  usage_count: number;
};

export default function Demo() {
  const [items, setItems] = useState<DemoSession[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [expiresHours, setExpiresHours] = useState(24);
  const [generated, setGenerated] = useState<{ url: string; token: string; label: string | null; expires_at: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function reload() {
    setError(null);
    try {
      const r = await api.listDemo();
      setItems(r.items);
    } catch (err: any) { setError(err?.message || 'Could not load.'); }
  }

  useEffect(() => { reload(); }, []);

  async function handleGenerate(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.generateDemo(label.trim() || undefined, expiresHours);
      setGenerated(r);
      setLabel('');
      await reload();
    } catch (err: any) {
      setError(err?.message || 'Could not generate.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRevoke(token: string) {
    if (!confirm(`Revoke demo URL? Anyone with the link will no longer be able to start a demo session.`)) return;
    try {
      await api.revokeDemo(token);
      await reload();
    } catch (err: any) {
      setError(err?.message || 'Revoke failed.');
    }
  }

  return (
    <div className="max-w-4xl">
      <h1 className="font-display text-3xl font-medium text-ink mb-2">Demo</h1>
      <p className="font-serif text-base text-ink-soft mb-6 max-w-prose">
        Demo mode bypasses the manual approval workflow. Submissions auto-deliver synthesis directly to the operator. Use only for live presentations.
      </p>

      <section className="mb-10 bg-paper border border-rule p-5">
        <h2 className="font-display text-lg font-medium text-ink mb-4">Generate a new demo URL</h2>
        <form onSubmit={handleGenerate} className="grid sm:grid-cols-[2fr_1fr_auto] gap-3 items-end">
          <label className="block">
            <span className="font-mono text-[11px] uppercase tracking-wider text-ink-light mb-1 block">Context label (optional)</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Boston Chamber, May 18"
              className="w-full bg-cream border border-rule px-3 py-1.5 font-serif text-sm focus:outline-none focus:border-accent"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[11px] uppercase tracking-wider text-ink-light mb-1 block">Expires</span>
            <select
              value={expiresHours}
              onChange={(e) => setExpiresHours(parseInt(e.target.value, 10))}
              className="w-full bg-cream border border-rule px-3 py-1.5 font-serif text-sm focus:outline-none focus:border-accent"
            >
              <option value={1}>1 hour</option>
              <option value={24}>24 hours</option>
              <option value={72}>72 hours</option>
              <option value={168}>1 week</option>
            </select>
          </label>
          <button type="submit" disabled={submitting} className="px-5 py-2 bg-ink text-paper font-serif text-sm hover:bg-accent disabled:opacity-40 transition-colors">
            {submitting ? 'Generating…' : 'Generate URL'}
          </button>
        </form>

        {generated && (
          <div className="mt-5 p-4 bg-cream-dark border-l-2 border-accent">
            <div className="font-mono text-[11px] uppercase tracking-wider text-ink-light mb-1">New demo URL</div>
            <div className="font-mono text-xs text-ink break-all bg-paper border border-rule px-3 py-2 mb-2">{generated.url}</div>
            <div className="flex gap-2">
              <button
                onClick={() => navigator.clipboard?.writeText(generated.url)}
                className="px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-ink-soft hover:text-accent border border-rule"
              >
                Copy URL
              </button>
              <span className="font-mono text-[11px] uppercase tracking-wider text-ink-light self-center">
                expires {new Date(generated.expires_at).toLocaleString()}
                {generated.label && <> · label: <em>{generated.label}</em></>}
              </span>
            </div>
          </div>
        )}
      </section>

      {error && <p className="font-serif text-accent mb-4">{error}</p>}

      <h2 className="font-display text-lg font-medium text-ink mb-4">Recent demo URLs</h2>
      {!items && <p className="font-mono text-[11px] uppercase tracking-wider text-ink-light">Loading…</p>}
      {items && items.length === 0 && <p className="font-serif text-ink-light italic">No demo URLs yet.</p>}
      {items && items.length > 0 && (
        <div className="overflow-x-auto border border-rule bg-paper">
          <table className="w-full font-serif text-sm">
            <thead>
              <tr className="bg-cream-dark text-left">
                <Th>Status</Th>
                <Th>Label</Th>
                <Th>Created</Th>
                <Th>Expires</Th>
                <Th>Used</Th>
                <Th>Token</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => {
                const expired = new Date(d.expires_at).getTime() < Date.now();
                return (
                  <tr key={d.token} className="border-t border-rule">
                    <td className="px-3 py-2">
                      <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 ${expired ? 'bg-red-50 text-red-800' : 'bg-emerald-50 text-emerald-800'}`}>
                        {expired ? 'expired' : 'active'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-ink">{d.label || <span className="italic text-ink-light">—</span>}</td>
                    <td className="px-3 py-2 text-ink-light font-mono text-[12px]">{new Date(d.created_at).toISOString().slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-3 py-2 text-ink-light font-mono text-[12px]">{new Date(d.expires_at).toISOString().slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-3 py-2 text-ink-soft font-mono text-[12px] text-right">{d.usage_count}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-ink-light truncate max-w-[120px]" title={d.token}>{d.token}</td>
                    <td className="px-3 py-2 text-right">
                      {!expired && (
                        <button onClick={() => handleRevoke(d.token)} className="font-mono text-[11px] uppercase tracking-wider text-accent hover:underline">
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-ink-light">{children}</th>;
}
